/**
 * Личный кабинет клиента (Figma 1:1381).
 */

import { useEffect, useState } from "react";
import type {
  AccessType,
  ClientAccessGrant,
  ClientAccessProject,
  ClientAccessResult,
} from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import grantCheckIcon from "../../assets/cabinet-grant-check.svg";
import { ClientLoginPage } from "./ClientLoginPage";
import {
  clearClientSession,
  readClientSession,
  writeClientSession,
  type ClientSession,
} from "./client-session";

const GRANT_CARDS: Array<{
  accessType: AccessType;
  title: string;
  hint: string;
}> = [
  { accessType: "price", title: "Прайсовая информация", hint: "Доступ по запросу" },
  { accessType: "bom", title: "Комплектовочные ведомости", hint: "Доступ по запросу" },
  {
    accessType: "reference",
    title: "Технические референсы и 3D-модели",
    hint: "Доступ по запросу",
  },
  { accessType: "traction_calc", title: "Экспортная документация", hint: "Доступ по запросу" },
];

type ClientCabinetPageProps = {
  api: PublicCatalogApi;
  onOpenProject: (project: ClientAccessProject) => void;
  onNewProject: () => void;
  onLogout?: () => void;
  onGoRegister?: () => void;
};

export function ClientCabinetPage({
  api,
  onOpenProject,
  onNewProject,
  onLogout,
  onGoRegister,
}: ClientCabinetPageProps) {
  const [session, setSession] = useState<ClientSession | null>(() => readClientSession());
  const [access, setAccess] = useState<ClientAccessResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<AccessType | null>(null);

  useEffect(() => {
    if (!session) {
      setAccess(null);
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .accessClient({ personalCode: session.personalCode })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setAccess(result);
        const nextSession: ClientSession = {
          personalCode: result.client.personalCode,
          clientId: result.client.id,
          fullName: result.client.fullName,
          company: result.client.company,
          position: result.client.position,
          region: result.client.region,
        };
        writeClientSession(nextSession);
        setSession(nextSession);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, session?.personalCode]);

  function handleLoggedIn(result: ClientAccessResult): void {
    const next: ClientSession = {
      personalCode: result.client.personalCode,
      clientId: result.client.id,
      fullName: result.client.fullName,
      company: result.client.company,
      position: result.client.position,
      region: result.client.region,
    };
    writeClientSession(next);
    setSession(next);
    setAccess(result);
  }

  function logout(): void {
    clearClientSession();
    setSession(null);
    setAccess(null);
    onLogout?.();
  }

  async function requestGrant(accessType: AccessType): Promise<void> {
    if (!session) {
      return;
    }
    setRequesting(accessType);
    setError(null);
    try {
      await api.requestCabinetAccessGrant({
        personalCode: session.personalCode,
        accessType,
      });
      const refreshed = await api.accessClient({ personalCode: session.personalCode });
      setAccess(refreshed);
    } catch (caught: unknown) {
      setError(describeError(caught));
    } finally {
      setRequesting(null);
    }
  }

  if (!session) {
    return (
      <ClientLoginPage
        api={api}
        onAccessSuccess={handleLoggedIn}
        {...(onGoRegister ? { onGoRegister } : {})}
        onGoWizard={onNewProject}
      />
    );
  }

  const grants = access?.grants ?? [];
  const projects = access?.projects ?? [];
  const hasPrice = grants.some((grant) => grant.accessType === "price" && grant.status === "active");
  const subtitle = [session.position, session.company, session.region].filter(Boolean).join(" · ");

  return (
    <section className="cabinet" aria-labelledby="cabinet-title">
      <header className="cabinet__header">
        <div className="cabinet__intro">
          <h1 id="cabinet-title" className="cabinet__title">
            Личный кабинет
          </h1>
          <p className="cabinet__subtitle">{subtitle || session.fullName}</p>
        </div>
        <div className="cabinet__header-aside">
          {hasPrice ? (
            <p className="cabinet__price-badge">
              <span className="cabinet__price-dot" aria-hidden="true" />
              Доступ к прайсовым данным
            </p>
          ) : null}
          <button type="button" className="cabinet__logout" onClick={logout}>
            Выйти
          </button>
        </div>
      </header>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {busy && !access ? <p className="wizard-step__status">Загрузка…</p> : null}

      <div className="cabinet__grid">
        <div className="cabinet__history">
          <div className="cabinet__section-head">
            <h2 className="cabinet__section-title">История проектов</h2>
            <button type="button" className="cabinet__new-project" onClick={onNewProject}>
              + Новый проект
            </button>
          </div>

          {projects.length === 0 && !busy ? (
            <div className="cabinet__empty">
              <p>Пока нет сохранённых проектов.</p>
              <button type="button" className="cabinet__new-project" onClick={onNewProject}>
                Начать подбор
              </button>
            </div>
          ) : (
            <div className="cabinet-table" role="table" aria-label="История проектов">
              <div className="cabinet-table__head" role="row">
                <span role="columnheader">Номер</span>
                <span role="columnheader">Модель / дата создания</span>
                <span role="columnheader">Статус</span>
                <span role="columnheader">Изменён</span>
              </div>
              {projects.map((project, index) => {
                const ui = projectRowUi(project, grants);
                return (
                  <button
                    key={project.id}
                    type="button"
                    className={
                      index % 2 === 1
                        ? "cabinet-table__row cabinet-table__row--alt"
                        : "cabinet-table__row"
                    }
                    role="row"
                    onClick={() => onOpenProject(project)}
                  >
                    <span className="cabinet-table__code" role="cell">
                      {formatProjectNumber(project)}
                    </span>
                    <span className="cabinet-table__model" role="cell">
                      <strong>{ui.modelLabel}</strong>
                      <span>Создан {formatDate(project.createdAt)}</span>
                    </span>
                    <span role="cell">
                      <span className={`cabinet-status cabinet-status--${ui.statusTone}`}>
                        {ui.statusLabel}
                      </span>
                    </span>
                    <span className="cabinet-table__changed" role="cell">
                      {formatDate(project.updatedAt)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="cabinet__grants">
          <div className="cabinet__section-head">
            <h2 className="cabinet__section-title">Уровни допуска</h2>
          </div>
          <ul className="cabinet-grants">
            {GRANT_CARDS.map((card) => {
              const grant = findGrant(grants, card.accessType);
              const active = grant?.status === "active";
              const pending = grant?.status === "pending";
              return (
                <li
                  key={card.accessType}
                  className={
                    active
                      ? "cabinet-grant cabinet-grant--active"
                      : "cabinet-grant"
                  }
                >
                  <span className="cabinet-grant__marker" aria-hidden="true" />
                  <div className="cabinet-grant__body">
                    <p className="cabinet-grant__title">{card.title}</p>
                    {!active ? (
                      <p className="cabinet-grant__hint">
                        {pending ? "Заявка на рассмотрении" : card.hint}
                      </p>
                    ) : null}
                  </div>
                  {active ? (
                    <img
                      src={grantCheckIcon}
                      alt=""
                      width={16}
                      height={16}
                      className="cabinet-grant__check"
                    />
                  ) : pending ? null : (
                    <button
                      type="button"
                      className="cabinet-grant__request"
                      disabled={requesting === card.accessType}
                      onClick={() => void requestGrant(card.accessType)}
                    >
                      Запросить
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

function findGrant(
  grants: ClientAccessGrant[],
  accessType: AccessType,
): ClientAccessGrant | undefined {
  return grants.find((grant) => grant.accessType === accessType);
}

function formatProjectNumber(project: ClientAccessProject): string {
  const year = new Date(project.createdAt).getFullYear().toString().slice(-2);
  const tail = project.id.replace(/-/g, "").slice(-3).toUpperCase();
  return `P-${year}-${tail}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function projectRowUi(
  project: ClientAccessProject,
  grants: ClientAccessGrant[],
): {
  modelLabel: string;
  statusLabel: string;
  statusTone: "ready" | "pending" | "work";
} {
  const resultStep = project.steps.find((step) => step.stepType === "result");
  const result = resultStep?.payload.result;
  let modelLabel = "Черновик";
  if (result && typeof result === "object") {
    const record = result as Record<string, unknown>;
    const name = typeof record.modelName === "string" ? record.modelName : null;
    const unit = typeof record.unitCode === "string" ? record.unitCode : null;
    if (name && unit) {
      modelLabel = `${name} · ${unit}`;
    } else if (name) {
      modelLabel = name;
    } else if (unit) {
      modelLabel = unit;
    }
  }

  if (project.status === "completed") {
    return { modelLabel, statusLabel: "Расчёт готов", statusTone: "ready" };
  }
  const pendingTraction = grants.some(
    (grant) => grant.accessType === "traction_calc" && grant.status === "pending",
  );
  if (pendingTraction) {
    return { modelLabel, statusLabel: "На согласовании", statusTone: "pending" };
  }
  return { modelLabel, statusLabel: "В работе", statusTone: "work" };
}

function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Не удалось загрузить кабинет";
}
